import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LabelList
} from "recharts";
import { OPTION_COLORS } from "./constants";
import { CustomTooltip, Bar3D, renderLegend } from "./ChartComponents";
import { getAgeChartData, translateSegment } from "./dataUtils";

export function AgeTab({ results, options, optionColorMap }) {
  const { t } = useTranslation();
  const ageChartData = getAgeChartData(results).map(row => ({
    ...row,
    name: translateSegment(t, row.name)
  }));

  return (
    <div className="space-y-6" data-testid="age-tab">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <Calendar className="h-5 w-5 text-slate-500" />
          <h2 className="text-lg font-heading font-semibold text-slate-900">{t('results.distributionByAge')}</h2>
        </div>
        <div className="h-[550px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ageChartData} margin={{ top: 30, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fill: '#374151', fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
              <YAxis tick={{ fill: '#374151', fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Legend content={renderLegend} />
              {options.map((option, index) => (
                <Bar key={option} dataKey={option} fill={optionColorMap[option] || OPTION_COLORS[index]} stackId="a" shape={<Bar3D />}>
                  <LabelList dataKey={option} position="center" fill="#ffffff" fontSize={10} fontWeight="bold" formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">{t('results.detailByAge')}</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('results.segment')}</th><th className="text-right">{t('results.total')}</th>
                {options.map((option, index) => (
                  <th key={option} className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span>{option}</span>
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: optionColorMap[option] || OPTION_COLORS[index] }} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ageChartData.map((row) => (
                <tr key={row.name}>
                  <td className="font-medium text-sm">{row.name}</td>
                  <td className="text-right font-mono text-sm font-semibold">{row.total}</td>
                  {options.map((option) => (<td key={option} className="text-right font-mono text-sm">{row[option] || 0}</td>))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td className="font-semibold">{t('results.total')}</td>
                <td className="text-right font-mono font-semibold">{results?.total_votes}</td>
                {options.map(option => (<td key={option} className="text-right font-mono font-semibold">{results?.results?.[option] || 0}</td>))}
              </tr>
            </tfoot>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
